import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  Link,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { normalizeDomain, looksLikeDomain, type Tier } from '@traaker/shared';
import {
  TIER_STASH_KEY,
  BRAND_STASH_KEY,
  GCLID_STASH_KEY,
  UTM_SOURCE_STASH_KEY,
  UTM_CAMPAIGN_STASH_KEY,
} from './Signup';

type AccountType = 'company' | 'agency';

/**
 * Post-verification tenant onboarding: a couple of identity fields, then
 * provision → celebratory transition → brand-onboarding wizard.
 *
 * The account type (Entreprise → basic trial vs Agence → agency credit trial)
 * is DERIVED from the plan chosen upstream on the pricing pages (?tier= on
 * /signup, stashed across the Cognito redirect) — there is no in-app type
 * picker: the essai-gratuit funnel is entreprise by definition, and agencies
 * arrive from tarifs-agences with ?tier=agency. A discreet link under the
 * form lets a misrouted visitor switch.
 *
 * No website-domain field here — the brand URL is collected in the next step
 * (the brand-onboarding wizard).
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshProfile } = useAuth();

  const [step, setStep] = useState<'details' | 'celebrate'>('details');
  const [accountType, setAccountType] = useState<AccountType>(() =>
    localStorage.getItem(TIER_STASH_KEY) === 'agency' ? 'agency' : 'company',
  );
  const [ownerName, setOwnerName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [agencyDomain, setAgencyDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isAgency = accountType === 'agency';
  const tier: Tier = isAgency ? 'agency' : 'basic';

  // The agency domain must be a real hostname, not just non-empty: it's the
  // agency's identity key downstream (CRM, favicon), and their brands are CLIENT
  // domains so there's nothing to fall back to. Server enforces the same rule.
  const agencyDomainNormalized = normalizeDomain(agencyDomain);
  const agencyDomainValid = looksLikeDomain(agencyDomainNormalized);
  const agencyDomainError = agencyDomain.trim().length > 0 && !agencyDomainValid;

  const detailsValid = isAgency
    ? orgName.trim() && agencyDomainValid
    : ownerName.trim() && orgName.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailsValid) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/tenants/onboard', {
        name: orgName.trim(),
        // Agency: the agency's own domain, normalized (the API normalizes again
        // — this just means what we send matches what gets stored). Company:
        // empty — the site URL is collected in the brand-onboarding step that
        // follows, and the CRM sync derives the domain from that brand's URL.
        domain: isAgency ? agencyDomainNormalized : '',
        tier,
        ownerName: ownerName.trim() || orgName.trim(),
        // Ad attribution stashed on /signup before the Cognito redirect. Sent
        // only when something was captured, so an organic signup stores no
        // empty object.
        ...(() => {
          const gclid = localStorage.getItem(GCLID_STASH_KEY) || undefined;
          const utmSource = localStorage.getItem(UTM_SOURCE_STASH_KEY) || undefined;
          const utmCampaign = localStorage.getItem(UTM_CAMPAIGN_STASH_KEY) || undefined;
          return gclid || utmSource || utmCampaign
            ? { attribution: { gclid, utmSource, utmCampaign } }
            : {};
        })(),
      });
      localStorage.removeItem(TIER_STASH_KEY);
      // Consumed — clear so a later, unrelated signup in this browser can't
      // inherit this visit's attribution.
      localStorage.removeItem(GCLID_STASH_KEY);
      localStorage.removeItem(UTM_SOURCE_STASH_KEY);
      localStorage.removeItem(UTM_CAMPAIGN_STASH_KEY);
      await refreshProfile();
      // Celebratory beat, then into the brand wizard.
      setStep('celebrate');
    } catch (err: any) {
      setError(err?.data?.message || err.message || t('onboarding.account.createError'));
      setSubmitting(false);
    }
  };

  // After the celebrate screen shows, route to the first-brand wizard.
  useEffect(() => {
    if (step !== 'celebrate') return;
    const t = setTimeout(() => {
      // Brand-hint prefill (from the marketing funnel) flows into the wizard.
      // Consumed — clear so a later signup in this browser can't inherit it.
      const brandHint = localStorage.getItem(BRAND_STASH_KEY) || '';
      localStorage.removeItem(BRAND_STASH_KEY);
      navigate(brandHint ? `/onboarding/brand/new?brand=${encodeURIComponent(brandHint)}` : '/onboarding/brand/new', {
        replace: true,
      });
    }, 2600);
    return () => clearTimeout(t);
  }, [step, navigate]);

  return (
    <Shell>
      {step === 'celebrate' ? (
        <CelebrateScreen isAgency={isAgency} />
      ) : (
        <Card sx={{ maxWidth: 560, width: '100%', borderRadius: 3, boxShadow: '0 12px 40px rgba(17,17,34,0.10)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
              {isAgency ? t('onboarding.details.agencyTitle') : t('onboarding.details.companyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {isAgency ? t('onboarding.details.agencySubtitle') : t('onboarding.details.companySubtitle')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {(
              <Fade in>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    {isAgency ? (
                      <>
                        <TextField
                          fullWidth label={t('onboarding.details.agencyName')} value={orgName}
                          onChange={(e) => setOrgName(e.target.value)} required autoFocus
                        />
                        <TextField
                          fullWidth label={t('onboarding.details.agencyDomain')} placeholder="agence.com" value={agencyDomain}
                          onChange={(e) => setAgencyDomain(e.target.value)} required
                          error={agencyDomainError}
                          helperText={
                            agencyDomainError
                              ? t('onboarding.details.agencyDomainInvalid')
                              : t('onboarding.details.agencyDomainHelper')
                          }
                        />
                        <TextField
                          fullWidth label={t('onboarding.details.yourNameOptional')} value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)}
                        />
                      </>
                    ) : (
                      <>
                        <TextField
                          fullWidth label={t('onboarding.details.yourName')} value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)} required autoFocus
                        />
                        <TextField
                          fullWidth label={t('onboarding.details.companyName')} value={orgName}
                          onChange={(e) => setOrgName(e.target.value)} required
                        />
                      </>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button type="submit" variant="contained" size="large" disabled={submitting || !detailsValid}>
                      {submitting ? t('onboarding.details.creating') : t('onboarding.details.continue')}
                    </Button>
                  </Stack>

                  {/* Escape hatch for a misrouted visitor — the type is otherwise
                      decided by which pricing page they came from. */}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3, textAlign: 'center' }}>
                    {isAgency ? t('onboarding.details.switchToCompany') : t('onboarding.details.switchToAgency')}{' '}
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => { setAccountType(isAgency ? 'company' : 'agency'); setError(null); }}
                      sx={{ fontWeight: 600 }}
                    >
                      {isAgency ? t('onboarding.details.switchToCompanyCta') : t('onboarding.details.switchToAgencyCta')}
                    </Link>
                  </Typography>
                </form>
              </Fade>
            )}
          </CardContent>
        </Card>
      )}
    </Shell>
  );
}

/** Page chrome: themed gradient backdrop + the Traaker logo. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage:
          'radial-gradient(1000px 480px at 50% -8%, rgba(124,58,237,0.12), transparent 60%), ' +
          'linear-gradient(180deg, #FAFAFA 0%, #F4F2FB 100%)',
      }}
    >
      <Box component="img" src="/traaker-logo-compact.png" alt="Traaker" sx={{ height: 36, mb: 4 }} />
      {children}
    </Box>
  );
}

/** Brief celebratory beat between provisioning and the brand wizard. */
function CelebrateScreen({ isAgency }: { isAgency: boolean }) {
  const { t } = useTranslation();
  return (
    <Fade in>
      <Box sx={{ textAlign: 'center', position: 'relative' }}>
        <Confetti />
        <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.02em', mb: 1 }}>
          {t('onboarding.celebrate.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
          {isAgency ? t('onboarding.celebrate.agency') : t('onboarding.celebrate.company')}
        </Typography>
      </Box>
    </Fade>
  );
}

/** Lightweight CSS confetti burst — no dependency. */
function Confetti() {
  const COLORS = ['#7C3AED', '#A78BFA', '#10B981', '#F59E0B', '#0EA5E9'];
  const pieces = Array.from({ length: 28 }, (_, i) => i);
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
      {pieces.map((i) => {
        const left = (i * 37) % 100;
        const delay = (i % 7) * 0.08;
        const dur = 1.6 + ((i * 13) % 9) / 10;
        const color = COLORS[i % COLORS.length];
        const size = 6 + (i % 4) * 2;
        return (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: -10,
              left: `${left}%`,
              width: size,
              height: size * 1.6,
              bgcolor: color,
              borderRadius: '1px',
              opacity: 0,
              animation: `traaker-confetti ${dur}s ${delay}s ease-in forwards`,
              '@keyframes traaker-confetti': {
                '0%': { transform: 'translateY(0) rotate(0deg)', opacity: 1 },
                '100%': { transform: `translateY(320px) rotate(${(i % 2 ? 1 : -1) * 540}deg)`, opacity: 0 },
              },
            }}
          />
        );
      })}
    </Box>
  );
}
